<script setup lang="ts">
import { Ref, computed, inject } from 'vue';

import { TSDialogConfirm, useToast } from 'tsv2-library';

import { BorrowingTransactionRequest } from '@/types/borrowing.type';

const toast = useToast();

const formData = inject('formData') as Ref<
  Map<string, BorrowingTransactionRequest>
>;

const visible = defineModel<boolean>('visible', { required: true });
const selectedData = defineModel<BorrowingTransactionRequest[]>(
  'selectedData',
  {
    required: true,
  },
);

/**
 * Handle the confirmation to remove asset from pre-list.
 */
const handleConfirm = async (): Promise<void> => {
  try {
    selectedData.value.forEach((datum) => {
      formData.value.delete(datum._id);
    });

    selectedData.value = [];

    toast.add({
      message: 'Success, asset has been removed from list.',
      severity: 'success',
    });
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to remove asset from list.',
      severity: 'error',
    });
  }
};

const isSelectedHasLinkedAsset = computed<boolean>(() => {
  return selectedData.value.some((d) => d.children && d.children.length > 0);
});
</script>
<template>
  <TSDialogConfirm
    v-if="isSelectedHasLinkedAsset"
    v-model:visible="visible"
    @confirm="handleConfirm"
    close-after-confirm
    confirm-label="Remove"
    data-ts-name="remove-from-list"
    header="Remove from List"
    severity="danger"
  >
    <template #body>
      <ul>
        <li :key="datum._id" v-for="datum in selectedData">
          {{ datum.name.nameWithSequence }}
        </li>
      </ul>
      <span> The following assets have other assets linked to them: </span>
      <div
        :key="datum._id"
        v-for="datum in selectedData.filter(
          (dat) => dat.children && dat.children.length > 0,
        )"
        class="flex flex-col"
      >
        <span>{{ datum.name.nameWithSequence }}:</span>
        <ul>
          <li :key="d._id" v-for="d in datum.children">
            {{ d.name.nameWithSequence }}
          </li>
        </ul>
      </div>
      <span>
        All related data will be removed from the list. Are you want to remove
        it?
      </span>
    </template>
  </TSDialogConfirm>
  <TSDialogConfirm
    v-model:visible="visible"
    :lists="selectedData.map((datum) => datum.name.nameWithSequence)"
    @confirm="handleConfirm"
    body="Are you sure want to remove it?"
    close-after-confirm
    confirm-label="Remove"
    data-ts-name="remove-from-list"
    header="Remove from List"
    severity="danger"
  />
</template>
